import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Dropdown, Input, Subtitle, Title } from '../../components/shared';
import { ILineResource } from '../../entities/farms.entities';
import { Table } from 'antd';
import useMenuHandler from '../../lib/useMenuHandler';
import { selectFarmsData } from '../../store/farms/farms.selector';
import { selectAccount } from '../../store/auth/auth.selector';
import {
  calcLineLastSize,
  getEstHarvestDate,
  getLineStatus,
} from '../../lib/farm.helpers';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import { TLang } from '../../entities/ui.entities';
import moment from 'moment';
import { selectAllTags } from '../../store/extra/extra.selector';
import {
  calcDiffDays,
  toMillisecond,
  defaultDateFormat,
  amountDays,
  displayValueUnit,
} from '../../lib/common.helpers';
import { formatNumber } from '../../entities/util-functions';
import { StageLabelSpan } from '../../components/CommonViews';
import { TBusinessType } from '../../entities/general.entities';
import SelectCropType from '../../components/farmform/SelectCropType';
import { CROP_COLORS } from '../../lib/constant';
import TaxonomyGroupFilter from '../../components/farm-util/TaxonomyGroupFilter';

const getLastAssDate = (line: ILineResource) =>
  line.growing_cycle?.last_assessment?.assessment_date ?? null;

const sizeRange = (line: ILineResource, unitType: any) => {
  const shellSize = calcLineLastSize(line);
  return shellSize
    ? `${displayValueUnit(
        shellSize.min,
        unitType,
        'size_mm',
      )}-${displayValueUnit(shellSize.max, unitType, 'size_mm')}`
    : '-';
};

const compareReady = (a: ILineResource, b: ILineResource) => {
  if (a.growing_cycle) {
    if (!b.growing_cycle) return -1;

    if (a.growing_cycle.ready_harvest) {
      return b.growing_cycle.ready_harvest
        ? a.growing_cycle.ready_harvest - b.growing_cycle.ready_harvest
        : -1;
    } else {
      if (b.growing_cycle.ready_harvest) return 1;

      const adt = getEstHarvestDate(a) ?? 0;
      const bdt = getEstHarvestDate(b) ?? 0;

      return adt - bdt;
    }
  } else {
    return b.growing_cycle ? 1 : 0;
  }
};

const options = (lang: TLang | undefined) => [
  { id: '0', label: translate(lang, 'All'), value: 'all' },
  { id: '1', label: translate(lang, 'Oldest line'), value: 'oldest_line' },
  {
    id: '2',
    label: translate(lang, 'Oldest assessment'),
    value: 'oldest_assessment',
  },
  {
    id: '3',
    label: translate(lang, 'Ready to harvest'),
    value: 'ready_harvest',
  },
  { id: '4', label: translate(lang, 'Catching Spat'), value: 'catch_spat' },
  {
    id: '5',
    label: translate(lang, 'Needing Assessment'),
    value: 'require_assessment',
  },
  {
    id: '6',
    label: translate(lang, 'Recent Assessment'),
    value: 'recent_assessment',
  },
  { id: '7', label: translate(lang, 'Empty Lines'), value: 'empty_lines' },
];

const columns = (
  line_assess_expire_days: number,
  lang: TLang | undefined,
  unitType: any,
) => [
  {
    title: translate(lang, 'Farm'),
    key: 'farm',
    render: (x: ILineResource) => (
      <>
        <span className='farm-name'>{x.farm_name}</span>
        <br />
        <span className='farm-number'>{x.farm_number}</span>
      </>
    ),
    sorter: (a: ILineResource, b: ILineResource) =>
      a.farm_name.localeCompare(b.farm_name),
  },
  {
    title: translate(lang, 'Line'),
    key: 'line_name',
    render: (x: ILineResource) => (
      <div
        style={{
          textAlign: 'center',
          borderBottom: `5px solid ${CROP_COLORS[x.crop_type]}`,
        }}
      >
        {x.line_name}
      </div>
    ),
    sorter: (a: ILineResource, b: ILineResource) =>
      a.line_name.localeCompare(b.line_name),
    width: 80,
  },
  {
    title: `${translate(lang, 'Length')}/${translate(
      lang,
      'Number of containers',
    )}`,
    key: 'length',
    render: (x: any) => (
      <div className='text-center'>
        {x.growing_cycle?.current_line_length ??
          x.growing_cycle?.current_basket_count ??
          '-'}
      </div>
    ),
    sorter: (a: any, b: any) =>
      (a.growing_cycle?.current_line_length ??
        a.growing_cycle?.current_basket_count ??
        -1) -
      (b.growing_cycle?.current_line_length ??
        b.growing_cycle?.current_basket_count ??
        -1),
  },
  {
    title: translate(lang, 'Date seeded'),
    key: 'date_seeded',
    render: (x: ILineResource) => (
      <>
        {x.growing_cycle ? (
          defaultDateFormat(x.growing_cycle.main_seed.planned_date_seed)
        ) : (
          <div className='d-flex flex-wrap'>
            <span className='mnw-74 pr-4'>
              {translate(lang, 'Line empty for')}
            </span>
            <Subtitle size={5} color='black' align='left' fontWeight={600}>
              {amountDays(x.line_idle, lang)}
            </Subtitle>
          </div>
        )}
      </>
    ),
    sorter: (a: ILineResource, b: ILineResource) => {
      if (a.growing_cycle) {
        return b.growing_cycle
          ? a.growing_cycle.main_seed.planned_date_seed -
              b.growing_cycle.main_seed.planned_date_seed
          : 1;
      } else {
        return b.growing_cycle ? -1 : (a.line_idle ?? 0) - (b.line_idle ?? 0);
      }
    },
  },
  {
    title: translate(lang, 'Planned harvest date'),
    key: 'planned_harvest_date',
    render: (x: ILineResource) => (
      <span>{defaultDateFormat(getEstHarvestDate(x), '-')}</span>
    ),
    sorter: (a: ILineResource, b: ILineResource) =>
      (getEstHarvestDate(a) ?? 0) - (getEstHarvestDate(b) ?? 0),
  },
  {
    title: translate(lang, 'Last assessment date'),
    key: 'last_assessment',
    render: (line: ILineResource) => {
      const x = getLastAssDate(line);
      const d =
        getLineStatus(line, line_assess_expire_days) === 'REQUIRE_ASSESSMENT';
      return (
        <span className={d ? 'color-red' : ''}>
          {defaultDateFormat(x, '-')}
        </span>
      );
    },
    sorter: (a: ILineResource, b: ILineResource) =>
      (getLastAssDate(a) ?? -1) - (getLastAssDate(b) ?? -1),
  },
  {
    title: translate(lang, 'Ready to harvest'),
    key: 'ready_to_harvest',
    render: (x: ILineResource) => {
      if (x.growing_cycle) {
        const al = x.growing_cycle.last_assessment
          ? defaultDateFormat(
              x.growing_cycle.last_assessment.planned_date_harvest,
            )
          : null;
        const sl = defaultDateFormat(
          x.growing_cycle.main_seed.planned_date_harvest,
        );
        const el =
          al && al !== sl ? (
            <div style={{ color: 'purple' }}>
              {`${translate(lang, '_original_harvest_date')}: ${sl}`}
            </div>
          ) : (
            <></>
          );

        if (x.growing_cycle.ready_harvest) {
          const d = moment().diff(
            toMillisecond(x.growing_cycle.ready_harvest),
            'days',
          );
          return (
            <span>
              <div>
                {`Ready ${
                  d === 0
                    ? 'today'
                    : d < 0
                      ? `in ${-d} day(s)`
                      : `${d} day(s) ago`
                }`}
              </div>
              {el}
            </span>
          );
        } else {
          const d = calcDiffDays(getEstHarvestDate(x) ?? 0);
          return (
            <span>
              <div>
                {`Planned ${
                  d === 0
                    ? 'today'
                    : d < 0
                      ? `in ${-d} day(s)`
                      : `${d} day(s) ago`
                }`}
              </div>
              {el}
            </span>
          );
        }
      } else {
        return <span>-</span>;
      }
    },
    sorter: compareReady,
  },
  {
    title: translate(lang, '_ready_harvest_date'),
    key: 'ready_harvest_date',
    render: (x: ILineResource) =>
      x.growing_cycle?.ready_harvest ? (
        <span>
          {translate(
            lang,
            '_marked_ready',
            x.growing_cycle.last_assessment?.verifier?.name ??
              x.growing_cycle.last_assessment?.user?.name,
            defaultDateFormat(x.growing_cycle.ready_harvest),
          )}
        </span>
      ) : (
        <span>{'-'}</span>
      ),
    sorter: compareReady,
  },
  {
    title: translate(lang, 'Size Range'),
    key: 'size_range',
    render: (x: ILineResource) => <span>{sizeRange(x, unitType)}</span>,
    sorter: (a: ILineResource, b: ILineResource) =>
      (calcLineLastSize(a)?.avg ?? 0) - (calcLineLastSize(b)?.avg ?? 0),
  },
  {
    title: translate(lang, 'Stage'),
    key: 'stage',
    render: (x: ILineResource) => (
      <StageLabelSpan stage={x.growing_cycle?.main_seed.stage ?? 1} />
    ),
    sorter: (a: ILineResource, b: ILineResource) =>
      (a.growing_cycle?.main_seed.stage ?? 0) -
      (b.growing_cycle?.main_seed.stage ?? 0),
    align: 'center',
  } as any,
];

type optionType =
  | 'all'
  | 'oldest_line'
  | 'oldest_assessment'
  | 'ready_harvest'
  | 'catch_spat'
  | 'require_assessment'
  | 'recent_assessment'
  | 'empty_lines';

const LinesPage = () => {
  const query = new URLSearchParams(useLocation().search);
  const farms = useSelector(selectFarmsData);
  const lineTags = useSelector(selectAllTags);
  const lang = useSelector(selectLang);
  const account = useSelector(selectAccount);
  const unitType = account?.display_unit;

  const [selectedOption, setSelectedOption] = useState<optionType>(
    (query.get('filter') as any) ?? 'all',
  );
  const [sizeFrom, setSizeFrom] = useState('');
  const [sizeTo, setSizeTo] = useState('');
  const [filterName, setFilterName] = useState('');
  const [chkTags, setChkTags] = useState<number[]>();
  const [fltStage, setFltStage] = useState(0);
  const [cropType, setCropType] = useState<TBusinessType>();
  const [fltTaxonomyIds, setFltTaxonomyIds] = useState<number[]>([]);

  const filteredLines = useMemo(() => {
    let result: ILineResource[] = [];
    for (let farm of farms) {
      if (cropType) {
        result.push(...farm.lines.filter(x => x.crop_type === cropType));
      } else {
        result.push(...farm.lines);
      }
    }
    if (selectedOption === 'oldest_line') {
      result = result.filter(x => !!x.growing_cycle);
      result.sort((a, b) =>
        (a.growing_cycle?.main_seed.planned_date_seed ?? 0) <
        (b.growing_cycle?.main_seed.planned_date_seed ?? 0)
          ? -1
          : 1,
      );
    } else if (selectedOption === 'oldest_assessment') {
      result = result.filter(x => getLastAssDate(x) !== null);
      result.sort((a, b) =>
        (getLastAssDate(a) ?? 0) < (getLastAssDate(b) ?? 0) ? -1 : 1,
      );
    } else if (selectedOption === 'catch_spat') {
      result = result.filter(
        t => t.growing_cycle && t.growing_cycle.main_seed.is_catch_spat,
      );
      result.sort((a, b) =>
        (a.growing_cycle?.main_seed.planned_date_seed ?? 0) <
        (b.growing_cycle?.main_seed.planned_date_seed ?? 0)
          ? -1
          : 1,
      );
    } else if (selectedOption === 'ready_harvest') {
      result = result.filter(x => !!x.growing_cycle);
      result.sort(compareReady);
    } else if (selectedOption === 'require_assessment') {
      result = result.filter(
        x =>
          getLineStatus(x, account?.line_assess_expire_days) ===
          'REQUIRE_ASSESSMENT',
      );
    } else if (selectedOption === 'recent_assessment') {
      result = result.filter(x => {
        if (!x.growing_cycle?.last_assessment) return false;
        let d = Date.now() - toMillisecond(getLastAssDate(x));
        return d < 7 * 3600 * 24 * 1000;
      });
    } else if (selectedOption === 'empty_lines') {
      result = result.filter(x => !x.growing_cycle);
    }
    if (sizeFrom.length > 0) {
      result = result.filter(x => {
        const min = calcLineLastSize(x)?.min;
        return (min ?? 0) >= parseInt(sizeFrom);
      });
    }
    if (sizeTo.length > 0) {
      result = result.filter(x => {
        const max = calcLineLastSize(x)?.max;
        return (max ?? 0) <= parseInt(sizeTo);
      });
    }
    if (chkTags && chkTags.length > 0 && lineTags) {
      const tags = lineTags
        .filter(x => chkTags.includes(x.id))
        .map(t => t.tag_lines)
        .reduce((p, c) => [...p, ...c], [])
        .map(t => t.line_id);
      result = result.filter(x => tags.includes(x.id));
    }
    if (filterName.length > 0) {
      result = result.filter(x =>
        x.line_name.toLowerCase().includes(filterName.toLowerCase()),
      );
    }
    if (fltStage) {
      result = result.filter(
        x => x.growing_cycle && x.growing_cycle.main_seed.stage === fltStage,
      );
    }
    if (fltTaxonomyIds.length > 0) {
      result = result.filter(line =>
        line.growing_cycle?.taxonomy_groups?.some(tg =>
          fltTaxonomyIds.includes(tg.id),
        ),
      );
    }
    return result;
  }, [
    sizeFrom,
    sizeTo,
    selectedOption,
    farms,
    account,
    chkTags,
    filterName,
    fltStage,
    cropType,
    fltTaxonomyIds,
  ]);

  const { redirectToLine } = useMenuHandler();

  return (
    <div className='lines-page'>
      <div className='h-calc-80 bg-secondary'>
        <div className='container pos-relative'>
          <div className='farms'>
            <div className='farms__header d-flex justify-between align-center'>
              <Title size={5} color='black-3' align='default' fontWeight={700}>
                {translate(lang, 'Lines')}
              </Title>
            </div>
            <div className='farms__content'>
              <div className='farms__line--header__name mb-17'>
                <div className='d-flex justify-between align-end'>
                  <div className='d-flex'>
                    <div>
                      <Input
                        type='number'
                        label={translate(lang, 'Search by Size From')}
                        value={sizeFrom}
                        placeholder=''
                        onChange={e => setSizeFrom(e.target.value)}
                        unit='mm'
                      />
                    </div>
                    <div className='ml-12'>
                      <Input
                        type='number'
                        label={translate(lang, 'Search by Size To')}
                        value={sizeTo}
                        placeholder=''
                        onChange={e => setSizeTo(e.target.value)}
                        unit='mm'
                      />
                    </div>
                    <div className='ml-12'>
                      <Input
                        type='text'
                        label={translate(lang, 'Search by Line name')}
                        value={filterName}
                        placeholder=''
                        onChange={e => setFilterName(e.target.value)}
                      />
                    </div>
                    <div className='ml-12'>
                      <SelectCropType
                        className='filter-option'
                        value={cropType}
                        onChange={v => setCropType(v)}
                        showAll={true}
                      />
                    </div>
                  </div>
                  <Dropdown
                    className='filter-option'
                    label=''
                    value={selectedOption}
                    options={options(lang)}
                    onChange={(v: any) => setSelectedOption(v)}
                  />
                </div>
                <div className='mt-7 d-flex align-center'>
                  <div className='mr-10' style={{ flex: 1 }}>
                    <Dropdown
                      className='filter-option'
                      mode='multiple'
                      label={translate(lang, 'Line tags')}
                      options={lineTags?.map(x => ({
                        id: x.id.toString(),
                        value: x.id.toString(),
                        label: x.name,
                      }))}
                      value={chkTags?.map(t => t.toString()) as any}
                      onChange={(v: any) =>
                        setChkTags(v.map((x: any) => Number(x)))
                      }
                    />
                  </div>
                  <TaxonomyGroupFilter
                    className='wp-500'
                    value={fltTaxonomyIds}
                    onChange={setFltTaxonomyIds}
                  />
                  <div className='ml-10'>
                    <Dropdown
                      className='filter-option'
                      label={translate(lang, 'Stage')}
                      options={[
                        {
                          id: '0',
                          value: '0',
                          label: 'All',
                        },
                        ...[1, 2, 3, 4].map(x => ({
                          id: x.toString(),
                          value: x.toString(),
                          label: <StageLabelSpan stage={x} />,
                        })),
                      ]}
                      value={fltStage.toString()}
                      onChange={v => setFltStage(Number(v))}
                    />
                  </div>
                </div>
              </div>
              <div className='w-100 mt-17 pb-32'>
                <Table
                  rowKey='id'
                  className='table table--isFarm'
                  pagination={false}
                  columns={columns(
                    account?.line_assess_expire_days ?? 90,
                    lang,
                    unitType,
                  )}
                  dataSource={filteredLines}
                  onRow={r => ({
                    onClick: redirectToLine.bind(this, r.farm_id, r.id),
                  })}
                  scroll={{ y: 600 }}
                />
                <div className='mt-17 text-right mr-32 font-weight-600'>
                  {`${
                    !cropType || cropType === 'MUSSEL'
                      ? `Total length: ${displayValueUnit(
                          filteredLines.reduce(
                            (p, c: any) =>
                              p + (c.growing_cycle?.current_line_length ?? 0),
                            0,
                          ),
                          unitType,
                          'distance_m',
                        )}`
                      : ''
                  }${!cropType ? ', ' : ''}${
                    !cropType || cropType === 'OYSTER'
                      ? `Total containers: ${formatNumber(
                          filteredLines.reduce(
                            (p, c: any) =>
                              p + (c.growing_cycle?.current_basket_count ?? 0),
                            0,
                          ),
                        )}`
                      : ''
                  }`}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinesPage;
